import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Heart, Search, Menu, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useScrollLock } from "@/lib/scrollLock";
import Logo from "@/components/Logo";
import { prefetchCategoryProducts } from "@/lib/categoryPrefetch";

const scrollWindowToTop = () => {
  window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};

const NAV = [
  { to: "/category/ethnic", label: "Kurtis & Ethnic" },
  { to: "/category/coords", label: "Co-ords" },
  { to: "/category/dresses", label: "Dresses" },
  { to: "/category/tops", label: "Tops" },
];

const authLinkClass =
  "text-[10px] sm:text-[11px] uppercase tracking-[0.18em] link-underline whitespace-nowrap min-h-11 inline-flex items-center px-1 sm:px-1.5 hover:opacity-80 transition";

const authRegisterClass =
  "text-[10px] sm:text-[11px] uppercase tracking-[0.18em] border border-primary text-primary px-2.5 sm:px-3 py-1.5 min-h-9 inline-flex items-center whitespace-nowrap hover:bg-primary hover:text-primary-foreground transition-colors";

const AuthLinks = ({ user }) => {
  if (user) {
    const label = user.name?.split(" ")[0] || "Account";
    return (
      <Link to="/account" data-testid="account-link" className={authLinkClass} aria-label="Account">
        {label}
      </Link>
    );
  }

  return (
    <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
      <Link to="/login" data-testid="login-link" className={authLinkClass}>
        Log in
      </Link>
      <Link to="/register" data-testid="register-link" className={authRegisterClass}>
        Register
      </Link>
    </div>
  );
};

/** Compact auth + cart for mobile — cart always fully visible on Android/iOS. */
const MobileAuthControl = ({
  user,
  itemCount,
  wishlistCount,
  onCartOpen,
  onSearchOpen,
}) => {
  const cartButton = (
    <button
      type="button"
      data-testid="cart-toggle"
      className="relative h-9 w-9 inline-flex items-center justify-center hover:opacity-70 transition shrink-0"
      onClick={onCartOpen}
      aria-label="Cart"
    >
      <ShoppingBag className="w-[18px] h-[18px]" />
      {itemCount > 0 && (
        <span
          data-testid="cart-count"
          className="absolute top-0.5 right-0 text-[9px] bg-primary text-primary-foreground rounded-full min-w-[1rem] h-4 px-1 leading-4 text-center"
        >
          {itemCount}
        </span>
      )}
    </button>
  );

  if (user) {
    const label = (user.name || "there").trim().split(/\s+/)[0] || "there";
    return (
      <div
        className="flex flex-col items-end justify-center shrink-0 py-1"
        data-testid="mobile-auth-links"
      >
        <Link
          to="/account"
          data-testid="mobile-welcome"
          className="max-w-[7.5rem] truncate text-[9px] uppercase tracking-[0.12em] text-primary leading-none mb-0.5 hover:opacity-70 transition"
          aria-label={`Welcome ${label}, open account`}
          title={`Welcome, ${label}`}
        >
          Welcome, {label}
        </Link>
        <div className="flex items-center">
          <button
            type="button"
            data-testid="mobile-search-toggle"
            className="h-9 w-8 inline-flex items-center justify-center hover:opacity-70 transition shrink-0"
            onClick={onSearchOpen}
            aria-label="Search"
          >
            <Search className="w-[17px] h-[17px]" />
          </button>
          <Link
            to="/wishlist"
            data-testid="mobile-wishlist-link"
            className="relative h-9 w-8 inline-flex items-center justify-center hover:opacity-70 transition shrink-0"
            aria-label="Wishlist"
          >
            <Heart className="w-[17px] h-[17px]" />
            {wishlistCount > 0 && (
              <span
                data-testid="mobile-wishlist-count"
                className="absolute top-0.5 right-0 text-[9px] bg-primary text-primary-foreground rounded-full min-w-[1rem] h-4 px-1 leading-4 text-center"
              >
                {wishlistCount}
              </span>
            )}
          </Link>
          {cartButton}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center shrink-0" data-testid="mobile-auth-links">
      <Link
        to="/login"
        data-testid="mobile-login-link"
        className="text-[9px] uppercase tracking-[0.1em] text-primary h-11 inline-flex items-center px-0.5 hover:opacity-70 transition whitespace-nowrap"
      >
        Login
      </Link>
      <Link
        to="/register"
        data-testid="mobile-register-link"
        className="text-[9px] uppercase tracking-[0.1em] border border-primary text-primary px-1.5 py-1 h-7 inline-flex items-center whitespace-nowrap hover:bg-primary hover:text-primary-foreground transition-colors ml-0.5"
      >
        Join
      </Link>
      {cartButton}
    </div>
  );
};

const Header = () => {
  const { itemCount, setCartOpen, wishlist, user } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const prefetchNav = (to) => {
    const slug = to.split("/").pop();
    if (slug) prefetchCategoryProducts(slug);
  };

  useScrollLock(mobileOpen || searchOpen);

  const goHomeTop = (e) => {
    setMobileOpen(false);
    setSearchOpen(false);
    if (location.pathname === "/") {
      e.preventDefault();
      scrollWindowToTop();
      if (location.hash || location.search) {
        navigate("/", { replace: true });
      }
      return;
    }
    navigate("/");
  };

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    setSearchOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-background/90 backdrop-blur-xl border-b border-border" : "bg-background/60 backdrop-blur-md"
      }`}
    >
      <div className="max-w-[1480px] mx-auto pl-3 pr-[max(0.75rem,env(safe-area-inset-right))] sm:px-8 lg:px-12">
        <div className="h-24 lg:h-28 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-1.5 sm:gap-3 lg:flex lg:gap-8">
          {/* Mobile menu */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden -ml-1 h-11 w-10 inline-flex items-center justify-center shrink-0"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((open) => !open)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {/* Logo — same height; width capped only on very narrow Android screens */}
          <Link
            to="/"
            onClick={goHomeTop}
            data-testid="logo-link"
            className="min-w-0 h-full flex items-center justify-start lg:shrink-0"
            aria-label="Oori Label home"
          >
            <Logo variant="wordmark" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8 flex-1 min-w-0">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onMouseEnter={() => prefetchNav(n.to)}
                onFocus={() => prefetchNav(n.to)}
                onPointerDown={() => prefetchNav(n.to)}
                data-testid={`nav-${n.to.split("/").pop()}`}
                className={({ isActive }) =>
                  `text-[12px] uppercase tracking-[0.22em] link-underline whitespace-nowrap ${isActive ? "text-primary" : "text-primary/80"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* Right: Login / Join / Cart — reserved column so cart never clips */}
          <div className="flex items-center justify-end shrink-0 justify-self-end">
            <div className="hidden lg:flex items-center gap-1">
              <AuthLinks user={user} />
              <button
                data-testid="search-toggle"
                className="min-h-11 min-w-11 inline-flex items-center justify-center hover:opacity-70 transition"
                onClick={() => setSearchOpen((v) => !v)}
                aria-label="Search"
                aria-expanded={searchOpen}
              >
                <Search className="w-5 h-5" />
              </button>
              <Link
                to="/wishlist"
                data-testid="wishlist-link"
                className="relative min-h-11 min-w-11 inline-flex items-center justify-center hover:opacity-70 transition"
                aria-label="Wishlist"
              >
                <Heart className="w-5 h-5" />
                {wishlist.length > 0 && (
                  <span
                    data-testid="wishlist-count"
                    className="absolute -top-0.5 -right-0.5 text-[10px] bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 leading-none"
                  >
                    {wishlist.length}
                  </span>
                )}
              </Link>
              <button
                data-testid="cart-toggle"
                className="relative min-h-11 min-w-11 inline-flex items-center justify-center hover:opacity-70 transition"
                onClick={() => setCartOpen(true)}
                aria-label="Cart"
              >
                <ShoppingBag className="w-5 h-5" />
                {itemCount > 0 && (
                  <span
                    data-testid="cart-count"
                    className="absolute -top-0.5 -right-0.5 text-[10px] bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 leading-none"
                  >
                    {itemCount}
                  </span>
                )}
              </button>
            </div>
            <div className="lg:hidden flex items-center">
              <MobileAuthControl
                user={user}
                itemCount={itemCount}
                wishlistCount={wishlist.length}
                onCartOpen={() => setCartOpen(true)}
                onSearchOpen={() => setSearchOpen((v) => !v)}
              />
            </div>
          </div>
        </div>

        {/* Search inline */}
        {searchOpen && (
          <form onSubmit={submitSearch} className="pb-4 -mt-1" data-testid="search-form">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search for dresses, sets, tops…"
              data-testid="search-input"
              className="input-minimal serif text-lg sm:text-2xl placeholder:text-secondary/60"
            />
          </form>
        )}
      </div>

      {mobileOpen &&
        createPortal(
          <div className="fixed inset-0 z-[200] lg:hidden" data-testid="mobile-menu">
            <button
              type="button"
              className="absolute inset-0 bg-black/40"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
            />
            <div className="absolute inset-y-0 left-0 w-[min(100%,20rem)] bg-background shadow-2xl flex flex-col">
              <div className="flex items-center justify-between h-24 px-4 border-b border-border shrink-0">
                <Link to="/" onClick={goHomeTop} aria-label="Oori Label home">
                  <Logo variant="wordmark" />
                </Link>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="min-h-11 min-w-11 inline-flex items-center justify-center"
                  data-testid="mobile-menu-close"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-4 py-4 border-b border-border shrink-0">
                {user ? (
                  <Link
                    to="/account"
                    onClick={() => setMobileOpen(false)}
                    data-testid="mobile-menu-account-link"
                    className="btn-primary w-full justify-center min-h-11"
                  >
                    {user.name?.split(" ")[0] || "My account"}
                  </Link>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <Link
                      to="/login"
                      onClick={() => setMobileOpen(false)}
                      data-testid="mobile-menu-login-link"
                      className="btn-outline w-full justify-center min-h-11 text-[11px] tracking-[0.18em]"
                    >
                      Log in
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileOpen(false)}
                      data-testid="mobile-menu-register-link"
                      className="btn-primary w-full justify-center min-h-11 text-[11px] tracking-[0.18em]"
                    >
                      Register
                    </Link>
                  </div>
                )}
              </div>
              <nav className="p-6 flex flex-col gap-2 overflow-y-auto flex-1">
                <button
                  type="button"
                  data-testid="mobile-menu-search"
                  className="text-sm uppercase tracking-[0.22em] py-3 min-h-11 flex items-center gap-2 border-b border-border/60 w-full text-left"
                  onClick={() => {
                    setMobileOpen(false);
                    setSearchOpen(true);
                  }}
                >
                  <Search className="w-4 h-4" />
                  Search
                </button>
                {NAV.map((n) => (
                  <Link
                    key={n.to}
                    to={n.to}
                    onTouchStart={() => prefetchNav(n.to)}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-nav-${n.to.split("/").pop()}`}
                    className="serif text-2xl py-3 min-h-11 flex items-center border-b border-border/60 last:border-0"
                  >
                    {n.label}
                  </Link>
                ))}
                <div className="h-px bg-border my-4" />
                <Link
                  to="/wishlist"
                  onClick={() => setMobileOpen(false)}
                  className="text-sm uppercase tracking-[0.22em] py-3 min-h-11 flex items-center"
                >
                  Wishlist
                </Link>
                <Link
                  to="/about"
                  onClick={() => setMobileOpen(false)}
                  className="text-sm uppercase tracking-[0.22em] py-3 min-h-11 flex items-center"
                >
                  About
                </Link>
              </nav>
            </div>
          </div>,
          document.body
        )}
    </header>
  );
};

export default Header;

